import { microServicesRoutes } from '@constants';
import {
  type ApiResponse,
  type NumberPool,
  type NumbersListDto, type UpdateNumberV3Payload
} from '@types';
import { edgeApi } from '@lib/apiClients.ts';
import { handleError } from '@utils/handleAPIError.ts';

export default function useCallerIdNumbersEdgeApi (): {
  getNumbersAsync: (
    pageNumber: number,
    pageLimit: number,
    search: string,
    filters: string[],
  ) => Promise<ApiResponse<NumberPool[]>>;
  updateCallerIdAsync: (
    payload: UpdateNumberV3Payload
  ) => Promise<ApiResponse<NumberPool>>;
  deleteNumbersAsync: (
    numberId: string
  ) => Promise<ApiResponse<NumberPool>>;
  validateNumberAsync: (
    number: string,
    sdrId: string
  ) => Promise<ApiResponse<boolean>>;
  registryCallerId: (
    number: string,
    sdrId: string
  ) => Promise<ApiResponse<string>>;
  } {
  const getNumbersAsync = async (
    pageNumber: number,
    pageLimit: number,
    search: string,
    filters: string[],
  ): Promise<ApiResponse<NumberPool[]>> => {
    try {
      const res = await edgeApi.get<NumbersListDto>(
        microServicesRoutes.edge.get.numbers.callerId.numbers,
        { params: { page: pageNumber, pageLimit, search, filters } },
      );

      return {
        success: true,
        data: res.data?.data ?? [],
        totalPages: res.data?.totalPages,
        totalCount: res.data?.totalCount,
        pageNumber: res.data?.pageNumber,
      };
    } catch (e) {
      return handleError<NumberPool[]>('Error trying to get numbers', e, 'Edge getNumbers');
    }
  };

  const deleteNumbersAsync = async (
    numberId: string
  ): Promise<ApiResponse<NumberPool>> => {
    try {
      const res = await edgeApi.delete<NumberPool>(
        `${microServicesRoutes.edge.delete.twilio.deleteNumberUser}/${numberId}`,
      );

      return {
        success: true,
        data: res.data ?? [],
      };
    } catch (e) {
      return handleError<NumberPool>('Error trying to delete user numbers', e, 'Edge getNumbers');
    }
  };

  const updateCallerIdAsync = async (
    payload: UpdateNumberV3Payload,
  ): Promise<ApiResponse<NumberPool>> => {
    try {
      const res = await edgeApi.put<NumberPool>(
        `${microServicesRoutes.edge.put.twilio.updateNumber}/${payload.id}`,
        payload
      );

      return {
        success: true,
        data: res.data ?? [],
      };
    } catch (e) {
      return handleError<NumberPool>('Error trying to delete user numbers', e, 'Edge getNumbers');
    }
  };

  const registryCallerId = async (
    number: string,
    sdrId: string,
  ): Promise<ApiResponse<string>> => {
    try {
      const res = await edgeApi.post<string>(
        `${microServicesRoutes.edge.post.twilio.registryCallerId}/${sdrId}`,
        { number }
      );

      return {
        success: true,
        data: res.data,
      };
    } catch (e) {
      return handleError<string>('Error trying to call number', e, 'Edge getNumbers');
    }
  };

  const validateNumberAsync = async (
    number: string,
    sdrId: string,
  ): Promise<ApiResponse<boolean>> => {
    try {
      const res = await edgeApi.post<boolean>(
        `${microServicesRoutes.edge.post.twilio.validateNumber}/${sdrId}`,
        { number }
      );

      return {
        success: true,
        data: res.data,
      };
    } catch (e) {
      return handleError<boolean>('Error trying to validate user numbers', e, 'Edge getNumbers');
    }
  };

  return {
    getNumbersAsync,
    deleteNumbersAsync,
    updateCallerIdAsync,
    registryCallerId,
    validateNumberAsync,
  };
}